import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { accessoriesApi } from '../api';
import toast from 'react-hot-toast';

export default function AccessoriesDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [accessory, setAccessory] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAccessory();
  }, [id]);

  const fetchAccessory = async () => {
    try {
      const res = await accessoriesApi.getById(id);
      console.log('API Response:', res);
      console.log('res.data:', res?.data);
      // Handle both response formats: { data: accessory } and { data: { data: accessory } }
      if (res?.data?.data?.accessory) {
        setAccessory(res.data.data.accessory);
      } else if (res?.data?.data) {
        setAccessory(res.data.data);
      } else if (res?.data) {
        setAccessory(res.data);
      }
    } catch (err) {
      console.error('Error fetching accessory:', err);
      toast.error(err.response?.data?.message || 'Failed to load accessory');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return '—';
    return new Date(date).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const getStockStatus = (accessory) => {
    if (!accessory) return 'unknown';
    const isLowStock = Number(accessory.quantityAvailable) <= Number(accessory.lowStockThreshold);
    return isLowStock ? 'low_stock' : 'in_stock';
  };

  const getStockStatusLabel = (status) => {
    switch (status) {
      case 'low_stock':
        return 'Low Stock';
      case 'in_stock':
        return 'In Stock';
      default:
        return 'Unknown';
    }
  };

  const getCategoryLabel = (category) => {
    switch (category) {
      case 'fastener':
        return 'Fastener';
      case 'lining':
        return 'Lining';
      case 'trim':
        return 'Trim';
      case 'thread':
        return 'Thread';
      case 'other':
        return 'Other';
      default:
        return category;
    }
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="loading-screen">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  if (!accessory) {
    return (
      <div className="page-container">
        <div className="empty-state">
          <div className="empty-icon">🧵</div>
          <p>Accessory not found</p>
          <Link to="/accessories" className="btn btn-primary">Back to Accessories</Link>
        </div>
      </div>
    );
  }

  const stockStatus = getStockStatus(accessory);

  return (
    <div className="page-container">
      {/* Header */}
      <div className="page-header">
        <div className="flex items-center gap-4">
          <Link to="/accessories" className="btn btn-secondary btn-sm">← Back</Link>
          <div>
            <h1 className="page-title" style={{ marginBottom: 4 }}>
              {accessory.name}
            </h1>
            <span className={`badge badge-${stockStatus === 'low_stock' ? 'cancelled' : 'ready'}`}>
              {getStockStatusLabel(stockStatus)}
            </span>
          </div>
        </div>
        <div className="flex gap-2">
          <button 
            className="btn btn-success btn-sm" 
            onClick={() => navigate(`/accessories?restock=${accessory.id}`)}
          >
            + Restock
          </button>
        </div>
      </div>

      <div className="grid-2" style={{ gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        {/* Left Column */}
        <div className="flex flex-col gap-4">
          {/* Accessory Info Card */}
          <div className="card p-2">
            <h3 className="card-title">Accessory Information</h3>
            <div className="info-grid">
              <div className="info-item">
                <span className="info-label">Name</span>
                <span className="info-value font-semibold">{accessory.name}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Category</span>
                <span className="info-value badge badge-stitching">{getCategoryLabel(accessory.category)}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Stock Status</span>
                <span className={`info-value badge badge-${stockStatus === 'low_stock' ? 'cancelled' : 'ready'}`}>
                  {getStockStatusLabel(stockStatus)}
                </span>
              </div>
              <div className="info-item">
                <span className="info-label">Unit</span>
                <span className="info-value">{accessory.unit}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Available Quantity</span>
                <span className="info-value font-semibold" style={{ color: stockStatus === 'low_stock' ? 'var(--danger)' : 'var(--success)' }}>
                  {Number(accessory.quantityAvailable).toFixed(2)} {accessory.unit}
                </span>
              </div>
              <div className="info-item">
                <span className="info-label">Low Stock Threshold</span>
                <span className="info-value">{accessory.lowStockThreshold} {accessory.unit}</span>
              </div>
            </div>
          </div>

          {/* Pricing Card */}
          <div className="card p-2">
            <h3 className="card-title">Pricing Details</h3>
            <div className="info-grid">
              <div className="info-item">
                <span className="info-label">Cost Per Unit</span>
                <span className="info-value font-semibold">₹{Number(accessory.costPerUnit).toFixed(2)}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Total Value</span>
                <span className="info-value font-semibold" style={{ color: 'var(--success)' }}>
                  ₹{(
                    Number(accessory.quantityAvailable) * Number(accessory.costPerUnit)
                  ).toLocaleString('en-IN')}
                </span>
              </div>
            </div>
          </div>

          {/* Supplier Card */}
          {accessory.supplier && (
            <div className="card p-2">
              <h3 className="card-title">Supplier Information</h3>
              <div className="info-grid">
                <div className="info-item">
                  <span className="info-label">Supplier</span>
                  <span className="info-value">{accessory.supplier}</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Right Column */}
        <div className="flex flex-col gap-4">
          {/* Quick Stats Card */}
          <div className="card p-2">
            <h3 className="card-title">Quick Information</h3>
            <div className="summary-table">
              <div className="summary-row">
                <span>Accessory ID</span>
                <span className="mono text-muted">#{accessory.id}</span>
              </div>
              <div className="summary-row">
                <span>Category</span>
                <span className="badge badge-stitching">{getCategoryLabel(accessory.category)}</span>
              </div>
              <div className="summary-row">
                <span>Stock Status</span>
                <span className={`badge badge-${stockStatus === 'low_stock' ? 'cancelled' : 'ready'}`}>
                  {getStockStatusLabel(stockStatus)}
                </span>
              </div>
              <div className="summary-row">
                <span>Low Stock Alert</span>
                <span>{stockStatus === 'low_stock' ? '⚠️ Yes' : '✓ No'}</span>
              </div>
              <div className="summary-row total">
                <span>Total Value</span>
                <span className="font-bold">
                  ₹{(
                    Number(accessory.quantityAvailable) * Number(accessory.costPerUnit)
                  ).toLocaleString('en-IN')}
                </span>
              </div>
            </div>
          </div>

          {/* Actions Card */}
          <div className="card p-2">
            <h3 className="card-title">Actions</h3>
            <div className="flex flex-col gap-2">
              <button 
                className="btn btn-secondary" 
                style={{ justifyContent: 'flex-start' }}
                onClick={() => navigate(`/accessories?restock=${accessory.id}`)}
              >
                📦 Restock Accessory
              </button>
              <button 
                className="btn btn-secondary" 
                style={{ justifyContent: 'flex-start' }}
                onClick={() => navigate(`/accessories?edit=${accessory.id}`)}
              >
                ✏️ Edit Accessory
              </button>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .grid-2 {
          display: grid;
        }
        @media (max-width: 1024px) {
          .grid-2 {
            grid-template-columns: 1fr !important;
          }
        }
        .flex {
          display: flex;
        }
        .flex-col {
          flex-direction: column;
        }
        .items-center {
          align-items: center;
        }
        .gap-2 {
          gap: 8px;
        }
        .gap-4 {
          gap: 16px;
        }
        .card-title {
          font-size: 1rem;
          font-weight: 600;
          color: var(--text-primary);
          margin-bottom: 16px;
          padding-bottom: 8px;
          border-bottom: 1px solid var(--border-color);
        }
        .info-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 12px;
        }
        .info-item {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }
        .info-label {
          font-size: 0.75rem;
          color: var(--text-muted);
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }
        .info-value {
          font-size: 0.9rem;
          color: var(--text-primary);
        }
        .summary-table {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }
        .summary-row {
          display: flex;
          justify-content: space-between;
          font-size: 0.9rem;
          padding: 8px 0;
          border-bottom: 1px solid var(--border-color);
        }
        .summary-row:last-child {
          border-bottom: none;
        }
        .summary-row.total {
          font-size: 1rem;
          padding-top: 12px;
          margin-top: 4px;
          border-top: 2px solid var(--border-color);
        }
        .badge-active { background: #d1fae5; color: #065f46; }
        .badge-inactive { background: #fee2e2; color: #991b1b; }
        .badge-pending { background: #fef3c7; color: #92400e; }
        .badge-stitching { background: #dbeafe; color: #1e40af; }
        .badge-qc { background: #e0e7ff; color: #3730a3; }
        .badge-ready { background: #d1fae5; color: #065f46; }
        .badge-delivered { background: #dcfce7; color: #166534; }
        .badge-cancelled { background: #fee2e2; color: #991b1b; }
        .badge-in_progress { background: #dbeafe; color: #1e40af; }
        .badge-done { background: #d1fae5; color: #065f46; }
      `}</style>
    </div>
  );
}
